import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cn } from "../lib/cn";

/**
 * HoverCard — popover that opens on hover or focus (desktop), tap on touch.
 *
 * Decision (June 17, 2026): we do NOT depend on ``@radix-ui/react-hover-card``.
 * It's a transitive lockfile entry but not a direct dep — and Popover already
 * gives us positioning, portal, escape-to-close, and focus-trap-free
 * interactive content. We layer the hover/focus open trigger via React state
 * + ``onPointerEnter`` / ``onPointerLeave`` / ``onFocus`` / ``onBlur`` so:
 *   • interactive content inside the surface stays keyboard-reachable
 *     (Radix Tooltip blocks pointer events on its content, breaking inline
 *     buttons like "Vedi nel contesto");
 *   • we keep DRY with the existing Popover primitive (single shadcn surface
 *     style — same border, shadow, radius, GPU-promoted animation);
 *   • mobile tap-to-open is the same gesture as a regular Popover, no Sheet
 *     fork needed.
 *
 * API mirrors shadcn's HoverCard (``HoverCard`` / ``HoverCardTrigger`` /
 * ``HoverCardContent``) so any existing shadcn example pastes 1:1 — only the
 * ``openDelay`` / ``closeDelay`` props are honoured (matching shadcn's
 * defaults of 700 / 300 ms).
 */

interface HoverCardContextValue {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly onTriggerEnter: () => void;
  readonly onTriggerLeave: () => void;
  readonly onContentEnter: () => void;
  readonly onContentLeave: () => void;
}

const HoverCardContext = React.createContext<HoverCardContextValue | null>(null);

function useHoverCardContext(): HoverCardContextValue {
  const ctx = React.useContext(HoverCardContext);
  if (!ctx) {
    throw new Error("HoverCard sub-components must be rendered inside <HoverCard>");
  }
  return ctx;
}

export interface HoverCardProps {
  /** Open delay in ms on pointer-enter / focus. Default 700 (shadcn). */
  readonly openDelay?: number;
  /** Close delay in ms on pointer-leave / blur. Default 300 (shadcn). */
  readonly closeDelay?: number;
  readonly children: React.ReactNode;
  readonly open?: boolean;
  readonly onOpenChange?: (open: boolean) => void;
}

export function HoverCard({
  openDelay = 700,
  closeDelay = 300,
  children,
  open: controlledOpen,
  onOpenChange,
}: HoverCardProps): React.ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = React.useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const setOpen = React.useCallback(
    (next: boolean) => {
      clearTimer();
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [clearTimer, isControlled, onOpenChange],
  );

  const scheduleOpen = React.useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(true), openDelay);
  }, [clearTimer, openDelay, setOpen]);

  const scheduleClose = React.useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setOpen(false), closeDelay);
  }, [clearTimer, closeDelay, setOpen]);

  React.useEffect(() => () => clearTimer(), [clearTimer]);

  const value = React.useMemo<HoverCardContextValue>(
    () => ({
      open,
      setOpen,
      onTriggerEnter: scheduleOpen,
      onTriggerLeave: scheduleClose,
      onContentEnter: clearTimer,
      onContentLeave: scheduleClose,
    }),
    [open, setOpen, scheduleOpen, scheduleClose, clearTimer],
  );

  return (
    <HoverCardContext.Provider value={value}>
      <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
        {children}
      </PopoverPrimitive.Root>
    </HoverCardContext.Provider>
  );
}

export interface HoverCardTriggerProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> {
  readonly asChild?: boolean;
}

export const HoverCardTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  HoverCardTriggerProps
>(({ onPointerEnter, onPointerLeave, onFocus, onBlur, onClick, ...props }, ref) => {
  const { open, setOpen, onTriggerEnter, onTriggerLeave } = useHoverCardContext();
  return (
    <PopoverPrimitive.Trigger
      ref={ref}
      {...props}
      onPointerEnter={(event) => {
        onPointerEnter?.(event);
        // Only pointer-enter from a true hover-capable device (mouse / stylus).
        // ``event.pointerType === "touch"`` would otherwise fire synthetic
        // enter→leave→up sequences on tap and race with the click handler.
        if (event.pointerType === "mouse" || event.pointerType === "pen") {
          onTriggerEnter();
        }
      }}
      onPointerLeave={(event) => {
        onPointerLeave?.(event);
        if (event.pointerType === "mouse" || event.pointerType === "pen") {
          onTriggerLeave();
        }
      }}
      onFocus={(event) => {
        onFocus?.(event);
        // Keyboard-focus opens immediately (no openDelay) — matches WAI-ARIA
        // tooltip pattern for keyboard users who can't "hover".
        setOpen(true);
      }}
      onBlur={(event) => {
        onBlur?.(event);
        onTriggerLeave();
      }}
      onClick={(event) => {
        onClick?.(event);
        // Tap-to-toggle on touch devices (where pointer-enter is suppressed
        // above). On desktop a click also toggles — that matches the user
        // expectation that the card is "clickable" UI, not pure decoration.
        setOpen(!open);
      }}
    />
  );
});
HoverCardTrigger.displayName = "HoverCardTrigger";

export interface HoverCardContentProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> {}

export const HoverCardContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  HoverCardContentProps
>(({ className, align = "center", sideOffset = 6, onPointerEnter, onPointerLeave, ...props }, ref) => {
  const { onContentEnter, onContentLeave } = useHoverCardContext();
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        onPointerEnter={(event) => {
          onPointerEnter?.(event);
          onContentEnter();
        }}
        onPointerLeave={(event) => {
          onPointerLeave?.(event);
          if (event.pointerType === "mouse" || event.pointerType === "pen") {
            onContentLeave();
          }
        }}
        // Don't steal focus on open (we're an info-surface, not a dialog) —
        // restores caret position on close to the trigger.
        onOpenAutoFocus={(event) => event.preventDefault()}
        className={cn(
          "z-50 w-72 rounded-md border border-hairline bg-popover p-3 text-popover-foreground shadow-md outline-none",
          // Same GPU-layer promotion comment lives on ``popover.tsx`` —
          // mirroring it here keeps the perceived-latency win identical.
          "transform-gpu will-change-[opacity,transform]",
          "data-[state=closed]:animate-fade-out data-[state=open]:animate-fade-in",
          className,
        )}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});
HoverCardContent.displayName = "HoverCardContent";
